import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import UserMasterLayout from '../../../Layouts/UserMasterLayout/UserMasterLayout';
import AppContext from '../../../context/AppContext';
import '../../../assests/css/customer/helpsupport/helpsupport.css';

const Dispute = () => {
  const { baseUrl, baseUrlForFiles, customer } = useContext(AppContext);

  const customerId = customer?.id || null;

  const [formData, setFormData] = useState({ subject: '', category: '', message: '' });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [activeTab, setActiveTab] = useState('messages');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const faqs = [
    { id: 1, question: 'How do I track my order?', answer: 'You can track your order by going to the "Orders" section in your account dashboard.' },
    { id: 2, question: 'What is the return policy?', answer: 'We offer a 30-day return policy for most items. Refunds are processed within 5-7 business days.' },
    { id: 3, question: 'How do I add a new product?', answer: 'Go to your seller dashboard and click "Add Product". Fill in all required information.' },
    { id: 4, question: 'When will I receive my payment?', answer: 'Payments are processed within 24-48 hours after order confirmation.' },
    { id: 5, question: 'Can I edit my shop information?', answer: 'Yes, go to "Settings" > "Shop Profile" to update your shop details.' },
  ];

  // ── Fetch all tickets ──
  const fetchAllTickets = () => {
    if (!baseUrl || !customerId) return;
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/HelpandSupport/fetchAllTickets/${customerId}`)
      .then((res) => { if (res.data.success) setTickets(res.data.tickets || []); else setTickets([]); })
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAllTickets(); }, [baseUrl, customerId]);

  // ── Fetch conversation + messages ──
  const fetchConversation = (ticket) => {
    if (!baseUrl) return;
    axios
      .post(`${baseUrl}/customer/HelpandSupport/fetchConversation`, {
        mlhelpsupport_id: ticket.id,
        mlcustomer_id: customerId,
        mlorder_id: ticket.Mlorder_id,
      })
      .then((response) => {
        if (response.data.success) {
          const conversation = response.data.conversation;
          setConversationId(conversation.id);
          const newMessages = conversation.Mlchats || conversation.mlchats || [];
          setMessages((prev) => {
            if (prev.length === newMessages.length) return prev;
            return newMessages;
          });
        }
      })
      .catch((error) => {
        console.error('fetchConversation error:', error);
      });
  };

  // ── Polling: har 3 second baad messages refresh ──
  useEffect(() => {
    if (!activeTicket) return;

    fetchConversation(activeTicket);

    const interval = setInterval(() => {
      fetchConversation(activeTicket);
    }, 3000);

    return () => clearInterval(interval);
  }, [activeTicket]);

  // ── Submit ticket ──
const handleSubmitTicket = (e) => {
  e.preventDefault();
  if (!customerId) {
    Swal.fire({ icon: 'error', title: 'Please wait', text: 'Loading your account, please try again in a moment', confirmButtonColor: '#c8a97e' });
    return;
  }
  if (!formData.subject || !formData.category || !formData.message) {
    Swal.fire({ icon: 'warning', title: 'Incomplete Form', text: 'Please fill in all fields', confirmButtonColor: '#c8a97e' });
    return;
  }
    axios
      .post(`${baseUrl}/customer/HelpandSupport/createTicket`, {
        Mlcustomer_id: customerId,
        subject: formData.subject,
        category: formData.category,
        description: formData.message,
      })
      .then((res) => {
        if (res.data.success) {
          Swal.fire({ icon: 'success', title: 'Submitted!', text: res.data.message, timer: 2000, showConfirmButton: false });
          setFormData({ subject: '', category: '', message: '' });
          fetchAllTickets();
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: res.data.message, confirmButtonColor: '#c8a97e' });
        }
      })
      .catch((err) => Swal.fire({ icon: 'error', title: 'Oops!', text: err.response?.data?.message || 'Something went wrong', confirmButtonColor: '#c8a97e' }));
  };

  // ── Delete ticket ──
  const handleDeleteTicket = (ticketId) => {
    Swal.fire({
      title: 'Are you sure?', text: 'This ticket will be permanently deleted!',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#c8a97e', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (!result.isConfirmed) return;
      axios
        .post(`${baseUrl}/customer/HelpandSupport/deleteTicket`, { ticket_id: ticketId, Mlcustomer_id: customerId })
        .then((res) => {
          if (res.data.success) {
            Swal.fire({ icon: 'success', title: 'Deleted!', text: res.data.message, timer: 1500, showConfirmButton: false });
            fetchAllTickets();
          } else {
            Swal.fire({ icon: 'error', title: 'Error', text: res.data.message, confirmButtonColor: '#c8a97e' });
          }
        })
        .catch((err) => Swal.fire({ icon: 'error', title: 'Oops!', text: err.response?.data?.message || 'Something went wrong', confirmButtonColor: '#c8a97e' }));
    });
  };

  // ── Image select for chat ──
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Send message (text and/or image) ──
  const handleSendMessage = () => {
    if (!message.trim() && !selectedImage) return;
    if (!conversationId) return;

    const payload = new FormData();
    payload.append('Mlconversation_id', conversationId);
    payload.append('message', message);
    if (selectedImage) payload.append('image', selectedImage);

    axios
      .post(`${baseUrl}/customer/HelpandSupport/sendMessage`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => {
        if (response.data.success) {
          setMessages((prev) => [...prev, response.data.message]);
          setMessage('');
          handleRemoveImage();
        }
      })
      .catch((error) => {
        console.error('sendMessage error:', error);
      });
  };

  const handleMessageKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  // ── Open ticket detail ──
  const openTicketDetail = (ticket) => {
    setActiveTicket(ticket);
    setActiveTab('messages');
    setMessages([]);
    setConversationId(null);
    handleRemoveImage();
  };

  const getStatusClass = (status) => {
    const map = { open: 'sp-status-open', pending: 'sp-status-progress', closed: 'sp-status-resolved', escalated: 'sp-status-open' };
    return map[(status || '').toLowerCase()] || 'sp-status-resolved';
  };

  // ── DETAIL VIEW ──
  if (activeTicket) {
    return (
      <UserMasterLayout>
        <div className="sp-container">

          <div className="sp-detail-breadcrumb">
            <span className="sp-detail-breadcrumb-link" onClick={() => setActiveTicket(null)}>My Disputes</span>
            <span className="sp-detail-breadcrumb-sep">/</span>
            <span className="sp-detail-breadcrumb-current">Case #{activeTicket.id}</span>
          </div>

          <div className="sp-detail-header">
            <div>
              <div className="sp-detail-title-row">
                <h1 className="sp-detail-title">Case #{activeTicket.id}</h1>
                <span className={`sp-status-badge ${getStatusClass(activeTicket.status)}`}>
                  {(activeTicket.status || '').toUpperCase()}
                </span>
              </div>
              <p className="sp-detail-subject">Subject: {activeTicket.subject || 'N/A'}</p>
            </div>
            <button className="sp-detail-back-btn" onClick={() => setActiveTicket(null)}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M12 6l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Back to list
            </button>
          </div>

          <div className="sp-detail-grid">

            {/* LEFT — Ticket Summary */}
            <div className="sp-card">
              <h3 className="sp-detail-summary-title">TICKET SUMMARY</h3>
              <div className="sp-detail-rows">
                {[
                  ['Ticket ID', `#${activeTicket.id}`],
                  ['Order ID', activeTicket.Mlorder_id ? `#${activeTicket.Mlorder_id}` : 'N/A'],
                  ['Category', activeTicket.category || 'N/A'],
                  ['Created', activeTicket.created_at ? new Date(activeTicket.created_at).toLocaleDateString() : 'N/A'],
                  ['Conversation', conversationId ? `#${conversationId}` : 'N/A'],
                ].map(([label, val]) => (
                  <div key={label} className="sp-detail-row">
                    <span className="sp-detail-row-label">{label}</span>
                    <span className="sp-detail-row-val">{val}</span>
                  </div>
                ))}
                <div className="sp-detail-row">
                  <span className="sp-detail-row-label">Status</span>
                  <span className={`sp-status-badge ${getStatusClass(activeTicket.status)}`}>
                    {(activeTicket.status || '').toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* RIGHT — Tabs */}
            <div className="sp-card sp-detail-messages-card">
              <div className="sp-detail-tabs">
                {['messages', 'description', 'timeline'].map((tab) => (
                  <button
                    key={tab}
                    className={`sp-detail-tab ${activeTab === tab ? 'sp-detail-tab--active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div className="sp-detail-msg-body">
                  <div className="sp-detail-msg-timestamp">
                    DISPUTE OPENED — {activeTicket.created_at ? new Date(activeTicket.created_at).toLocaleString() : 'N/A'}
                  </div>

                  <div className="sp-detail-msg-list">
                    {messages.length === 0 && (
                      <p className="sp-detail-msg-empty">No messages yet.</p>
                    )}
                    {messages.map((msg, i) => (
                      <div key={msg.id || i} className={`sp-detail-msg ${msg.sender === 'customer' ? 'sp-detail-msg--customer' : 'sp-detail-msg--support'}`}>
                        <div className="sp-detail-msg-header">
                          <span className="sp-detail-msg-sender">
                            {msg.sender === 'customer' ? 'You' : 'Admin Support'}
                          </span>
                          <span className="sp-detail-msg-time">
                            {msg.created_at
                              ? new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                              : ''}
                          </span>
                        </div>
                        {msg.image && (
                          <img
                            src={msg.image.startsWith('http') ? msg.image : `${baseUrlForFiles}/${msg.image}`}
                            alt="attachment"
                            className="sp-detail-msg-image"
                            onClick={() => window.open(msg.image.startsWith('http') ? msg.image : `${baseUrlForFiles}/${msg.image}`, '_blank')}
                          />
                        )}
                        {msg.message && <div className="sp-detail-msg-text">{msg.message}</div>}
                      </div>
                    ))}
                  </div>

                  {imagePreview && (
                    <div className="sp-detail-image-preview">
                      <img src={imagePreview} alt="preview" />
                      <button type="button" className="sp-detail-image-remove" onClick={handleRemoveImage}>×</button>
                    </div>
                  )}

                  <div className="sp-detail-input-bar">
                    <button className="sp-detail-attach-btn" onClick={() => fileInputRef.current?.click()}>
                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                        <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16z" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    </button>
                    <input type="file" ref={fileInputRef} accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />
                    <input
                      type="text"
                      className="sp-detail-msg-input"
                      placeholder="Write a message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={handleMessageKeyDown}
                    />
                    <button className="sp-detail-send-btn" onClick={handleSendMessage}>
                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                        <path d="M18 2L9 11M18 2l-7 16-2-7-7-2 16-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Description Tab */}
              {activeTab === 'description' && (
                <div className="sp-detail-desc">
                  <h4 className="sp-detail-desc-heading">Your Description</h4>
                  <p className="sp-detail-desc-text">{activeTicket.description || 'No description provided.'}</p>
                </div>
              )}

              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                <div className="sp-detail-timeline">
                  {[
                    ['Dispute Created', activeTicket.created_at],
                    ['Last Updated', activeTicket.updated_at],
                  ].map(([title, date]) => (
                    <div key={title} className="sp-detail-tl-item">
                      <div className="sp-detail-tl-dot"></div>
                      <div>
                        <div className="sp-detail-tl-title">{title}</div>
                        <div className="sp-detail-tl-time">{date ? new Date(date).toLocaleString() : 'N/A'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="sp-footer"><p className="sp-footer-text">© 2024 ShopSupport Portal. All rights reserved.</p></div>
        </div>
      </UserMasterLayout>
    );
  }

  // ── LIST VIEW ──
  return (
    <UserMasterLayout>
      <div className="sp-container">

        <div className="sp-header">
          <div className="sp-header-content">
            <div className="sp-logo">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="sp-logo-text">My Disputes</span>
            </div>
          </div>
        </div>

        <div className="sp-main-grid sp-main-grid--single">
          <div className="sp-card">
            <h2 className="sp-card-title">Submit a Support Ticket</h2>
            <form onSubmit={handleSubmitTicket} className="sp-form">
              <div className="sp-form-group">
                <label className="sp-label">Subject</label>
                <input type="text" className="sp-input" placeholder="Enter subject"
                  value={formData.subject} onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))} />
              </div>
              <div className="sp-form-group">
                <label className="sp-label">Category</label>
                <select className="sp-select" value={formData.category}
                  onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))}>
                  <option value="">Select Category</option>
                  <option value="Payment Problem">Payment Problem</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Refund">Refund</option>
                  <option value="Product Issue">Product Issue</option>
                  <option value="Account">Account</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="sp-form-group">
                <label className="sp-label">Message</label>
                <textarea className="sp-textarea" placeholder="Describe your issue in detail..." rows="6"
                  value={formData.message} onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))} />
              </div>
              <button type="submit" className="sp-submit-btn" disabled={!customerId}>
  {customerId ? 'Submit Ticket' : 'Loading...'}
</button>
            </form>
          </div>
        </div>

        <div className="sp-tickets-section">
          <h2 className="sp-section-title">Your Support Tickets</h2>
          <div className="sp-table-container">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Order ID</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>Loading...</td></tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#a09078' }}>No tickets found</td></tr>
                ) : (
                  tickets.map((ticket) => (
                    <tr key={ticket.id} className="sp-table-row">
                      <td><span className="sp-ticket-id">#{ticket.id}</span></td>
                      <td>{ticket.subject}</td>
                      <td>{ticket.category}</td>
                      <td><span className="sp-td-desc">{ticket.description}</span></td>
                      <td>{ticket.Mlorder_id ? `#${ticket.Mlorder_id}` : '—'}</td>
                      <td>
                        <span className={`sp-status-badge ${getStatusClass(ticket.status)}`}>
                          {(ticket.status || '').toUpperCase()}
                        </span>
                      </td>
                      <td>{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <div className="sp-action-buttons">
                          <button className="sp-action-btn sp-msg-btn" title="View ticket"
                            onClick={() => openTicketDetail(ticket)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                            </svg>
                          </button>
                          <button className="sp-action-btn sp-delete-btn" title="Delete ticket"
                            onClick={() => handleDeleteTicket(ticket.id)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="sp-table-footer">
            <span className="sp-table-count">Showing {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="sp-footer"><p className="sp-footer-text">© 2026 ShopSupport Portal. All rights reserved.</p></div>
      </div>
    </UserMasterLayout>
  );
};

export default Dispute;